import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Brain, TrendingUp, Users, FileText, Map, Calendar, Briefcase, CheckCircle, AlertTriangle, Target, Zap, Shield, Award, BookOpen, Workflow, Clock, Sparkles, BarChart3 } from 'lucide-react';
import type { AikupItem } from '../data/aikupData';
import BesoinsWizard from './BesoinsWizard';
import AméliorationContinueModal from './AméliorationContinueModal';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';

interface AikupModalProps {
  item: AikupItem | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function AikupModal({ item, isOpen, onClose }: AikupModalProps) {
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isAméliorationOpen, setIsAméliorationOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'ods' | 'cahier' | 'roadmap' | 'plan'>('ods');
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen && item?.id === 12 ? (lexDates['prise_besoin_ods_last_ai_analysis'] ?? null) : null;
  const departmentResponsibilitiesLastAnalysisDate = isOpen && item?.id === 25 ? (lexDates['responsabilites_ti_cyber_sa_last_ai_analysis'] ?? null) : null;
  
  if (!item) return null;
  
  const isComprehensionDesBesoins = item.id === 12;
  const isAméliorationContinue = item.id === 10;
  const isDepartmentResponsibilities = item.id === 25;
  const departmentResponsibilities = [
    {
      department: 'TI',
      responsibility: 'Opérations technologiques, infrastructure, support, disponibilité et continuité de service.',
      examples: 'Postes, serveurs, réseau, Microsoft 365, sauvegardes, incidents techniques, demandes de support.',
      clarification: 'Intervient quand le sujet touche l’exploitation ou le fonctionnement technique.',
    },
    {
      department: 'Cybersécurité',
      responsibility: 'Risques, contrôles, conformité, protection des accès, posture cyber et réponse aux incidents de sécurité.',
      examples: 'MFA, EDR, vulnérabilités, politiques de sécurité, alertes, audits, plans de remédiation.',
      clarification: 'Intervient quand le sujet touche le risque, la conformité ou la protection.',
    },
    {
      department: "Solutions d'affaires",
      responsibility: 'Analyse des besoins, priorisation, processus d’affaires, alignement client et livrables de valeur.',
      examples: 'ODS, processus client, exigences d’affaires, priorités, documentation, roadmap et communication de valeur.',
      clarification: 'Intervient quand le sujet touche le besoin, le processus ou la valeur d’affaires.',
    },
  ];

  const handleExplorerPlus = () => {
    if (isComprehensionDesBesoins) {
      setIsWizardOpen(true);
    } else if (isAméliorationContinue) {
      setIsAméliorationOpen(true);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="sticky top-0 bg-bz-dark text-white p-6 rounded-t-xl">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-2xl font-bold">{item.title}</h2>
                      {/* Badge AI si dans knowledge base */}
                      {item.inKnowledgeBase && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                          className="flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
                          title="Disponible dans AI Assistant"
                        >
                          <Brain className="w-4 h-4 text-purple-200" />
                          <span className="text-xs text-purple-100 font-semibold">AI</span>
                        </motion.div>
                      )}
                    </div>
                    <span className="px-3 py-1 bg-bz-blue text-white text-sm font-medium rounded-full">
                      {item.category}
                    </span>
                    {/* Date AI pour les tuiles suivies dans LEX */}
                    {isComprehensionDesBesoins && lastAnalysisDate && (
                      <div className="flex items-center gap-2 mt-2 text-xs text-white/70">
                        <Clock className="w-3.5 h-3.5" />
                        <span>Dernière analyse AI pour LEX: {formatLastAnalysisDate(lastAnalysisDate)}</span>
                      </div>
                    )}
                    {isDepartmentResponsibilities && departmentResponsibilitiesLastAnalysisDate && (
                      <div className="flex items-center gap-2 mt-2 text-xs text-white/70">
                        <Clock className="w-3.5 h-3.5" />
                        <span>Dernière analyse AI pour LEX: {formatLastAnalysisDate(departmentResponsibilitiesLastAnalysisDate)}</span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={onClose}
                    className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Description */}
                <div>
                  <p className="text-gray-700 text-lg">{item.description}</p>
                </div>

                {isComprehensionDesBesoins ? (
                  <>
                    {/* Hero Message */}
                    <div className="bg-gradient-to-r from-orange-600/20 via-red-600/20 to-orange-600/20 rounded-xl p-6 border-2 border-orange-500/50">
                      <h3 className="text-2xl font-bold text-center text-gray-900 mb-3">
                        🎯 Les 4 Livrables qui Transforment une Prise de Besoin en EXCELLENCE
                      </h3>
                      <p className="text-gray-700 text-center text-lg">
                        Comprendre les besoins, c'est bien. Les <strong className="text-orange-600">TRADUIRE en livrables concrets</strong>, c'est ça l'excellence BZ.
                      </p>
                    </div>

                    {/* Onglets pour les 4 Livrables */}
                    <div className="border-2 border-gray-200 rounded-xl overflow-hidden">
                      {/* Tabs Navigation */}
                      <div className="flex border-b border-gray-200 bg-gray-50">
                        <button
                          onClick={() => setActiveTab('ods')}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 font-semibold transition-all ${
                            activeTab === 'ods'
                              ? 'bg-orange-500 text-white'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          <Briefcase className="w-5 h-5" />
                          <span className="hidden sm:inline">ODS</span>
                        </button>
                        <button
                          onClick={() => setActiveTab('cahier')}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 font-semibold transition-all ${
                            activeTab === 'cahier'
                              ? 'bg-blue-500 text-white'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          <FileText className="w-5 h-5" />
                          <span className="hidden sm:inline">Cahier</span>
                        </button>
                        <button
                          onClick={() => setActiveTab('roadmap')}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 font-semibold transition-all ${
                            activeTab === 'roadmap'
                              ? 'bg-green-500 text-white'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          <Calendar className="w-5 h-5" />
                          <span className="hidden sm:inline">Roadmap</span>
                        </button>
                        <button
                          onClick={() => setActiveTab('plan')}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 font-semibold transition-all ${
                            activeTab === 'plan'
                              ? 'bg-purple-500 text-white'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          <Map className="w-5 h-5" />
                          <span className="hidden sm:inline">Plan</span>
                        </button>
                      </div>

                      {/* Tab Content */}
                      <div className="p-6 space-y-6">
                        
                        {/* ONGLET 1: ODS (Offre de Service) */}
                        {activeTab === 'ods' && (
                          <div className="space-y-6">
                            {/* Header */}
                            <div className="flex items-center gap-4 pb-4 border-b-2 border-orange-300">
                              <Briefcase className="w-10 h-10 text-orange-500" />
                              <div>
                                <h3 className="text-2xl font-bold text-gray-900">Offre de Service (ODS)</h3>
                                <p className="text-orange-600 font-semibold">Le PONT entre les besoins client et la valeur BZ</p>
                              </div>
                            </div>

                            {/* C'EST QUOI? */}
                            <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-lg">
                              <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                                <Target className="w-5 h-5 text-orange-600" />
                                C'EST QUOI?
                              </h4>
                              <p className="text-gray-700 leading-relaxed">
                                L'ODS, c'est le <strong>CONTRAT DE VALEUR</strong>. C'est ce que BZ s'engage à livrer, comment, et à quel prix. Point. Pas de flou. Pas d'"on verra". Du <strong>CONCRET mesurable</strong>.
                              </p>
                            </div>

                            {/* POURQUOI C'EST CRITIQUE? */}
                            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                              <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                                <Zap className="w-5 h-5 text-red-600" />
                                POURQUOI C'EST CRITIQUE?
                              </h4>
                              <p className="text-gray-700 leading-relaxed mb-3">
                                Sans ODS clair, le client ne sait pas CE QU'IL ACHÈTE. Et nous, on ne sait pas ce qu'on doit livrer. Résultat? <strong>Déceptions, dépassements, frustration.</strong>
                              </p>
                              <p className="text-gray-700 leading-relaxed font-bold">
                                Un bon ODS = Attentes alignées = Client heureux = Renouvellement garanti.
                              </p>
                            </div>

                            {/* QU'EST-CE QU'ON Y MET? */}
                            <div className="bg-white border-2 border-orange-200 rounded-lg p-4">
                              <h4 className="font-bold text-lg text-gray-900 mb-3">📋 QU'EST-CE QU'ON Y MET?</h4>
                              <ul className="space-y-2">
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <strong className="text-gray-900">Services offerts:</strong>
                                    <span className="text-gray-700"> Support, conseil stratégique, gestion de projets, veille techno...</span>
                                  </div>
                                </li>
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <strong className="text-gray-900">SLA (Service Level Agreement):</strong>
                                    <span className="text-gray-700"> Temps de réponse, disponibilité, escalade</span>
                                  </div>
                                </li>
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <strong className="text-gray-900">Livrables mensuels:</strong>
                                    <span className="text-gray-700"> Rapports, KPIs, rencontres comité</span>
                                  </div>
                                </li>
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <strong className="text-gray-900">Tarification:</strong>
                                    <span className="text-gray-700"> Formule claire (forfait? heures? succès?)</span>
                                  </div>
                                </li>
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <strong className="text-gray-900">Exclusions:</strong>
                                    <span className="text-gray-700"> Ce qu'on NE fait PAS (aussi important!)</span>
                                  </div>
                                </li>
                              </ul>
                            </div>

                            {/* EXEMPLE CONCRET */}
                            <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
                              <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                                ✅ EXEMPLE CONCRET
                              </h4>
                              <p className="text-sm text-gray-600 italic mb-2">PME 50 employés, secteur manufacturier:</p>
                              <div className="bg-white rounded p-3 text-sm space-y-1 text-gray-700">
                                <p>• <strong>Service:</strong> Directeur TI à temps partiel (2 jours/semaine)</p>
                                <p>• <strong>SLA:</strong> Réponse urgent &lt;2h, routine &lt;24h</p>
                                <p>• <strong>Livrables:</strong> Comité mensuel, roadmap trimestriel, veille techno</p>
                                <p>• <strong>Prix:</strong> 6 000$/mois forfait (inclus 16h/mois)</p>
                                <p>• <strong>Exclusion:</strong> Développement logiciel, support niveau 1</p>
                              </div>
                            </div>

                            {/* ERREURS À ÉVITER */}
                            <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4">
                              <h4 className="font-bold text-lg text-red-900 mb-3 flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5" />
                                ❌ ERREURS À ÉVITER
                              </h4>
                              <ul className="space-y-2 text-gray-700">
                                <li className="flex items-start gap-2">
                                  <span className="text-red-600 font-bold mt-0.5">✗</span>
                                  <span><strong>Trop vague:</strong> "On va vous aider avec votre TI" ← INUTILE!</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-red-600 font-bold mt-0.5">✗</span>
                                  <span><strong>Promesses irréalistes:</strong> "Disponible 24/7" si on ne peut pas livrer</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-red-600 font-bold mt-0.5">✗</span>
                                  <span><strong>Pas d'exclusions:</strong> Le client pensera qu'on fait TOUT</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-red-600 font-bold mt-0.5">✗</span>
                                  <span><strong>Prix flou:</strong> "Selon les besoins" ← Recette pour disaster</span>
                                </li>
                              </ul>
                            </div>

                            {/* GÉNÉRATION AUTOMATIQUE AVEC IA */}
                            <div className="bg-gradient-to-br from-purple-50 via-indigo-50 to-purple-50 border-2 border-purple-300 rounded-lg p-6">
                              <h4 className="font-bold text-xl text-gray-900 mb-4 flex items-center gap-3">
                                <Sparkles className="w-6 h-6 text-purple-600" />
                                Génération Automatique avec IA PRIsME
                              </h4>
                              <p className="text-gray-700 leading-relaxed mb-4">
                                PRIsME génère automatiquement l'ODS complet à partir de la prise de besoin remplie. 
                                L'IA analyse toutes les réponses et crée un document professionnel aligné sur les standards internationaux.
                              </p>
                              <div className="grid md:grid-cols-2 gap-4 mb-4">
                                <div className="bg-white rounded-lg p-4 border-2 border-purple-200">
                                  <h5 className="font-bold text-purple-900 mb-2 flex items-center gap-2">
                                    <Workflow className="w-5 h-5" />
                                    Workflow Automatique
                                  </h5>
                                  <ol className="text-sm text-gray-700 space-y-1 list-decimal list-inside">
                                    <li>Remplir la prise de besoin (5 phases)</li>
                                    <li>IA analyse les réponses</li>
                                    <li>Génération automatique ODS</li>
                                    <li>Document prêt en quelques minutes</li>
                                  </ol>
                                </div>
                                <div className="bg-white rounded-lg p-4 border-2 border-purple-200">
                                  <h5 className="font-bold text-purple-900 mb-2 flex items-center gap-2">
                                    <Award className="w-5 h-5" />
                                    Standards Intégrés
                                  </h5>
                                  <ul className="text-sm text-gray-700 space-y-1">
                                    <li>✓ ITIL 4 (Service Management)</li>
                                    <li>✓ COBIT 2019 (Gouvernance)</li>
                                    <li>✓ NIST CSF 2.0 (Sécurité)</li>
                                    <li>✓ ISO 27001 (SMSI)</li>
                                  </ul>
                                </div>
                              </div>
                            </div>

                            {/* STRUCTURE HOLISTIQUE DE L'ODS */}
                            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-6">
                              <h4 className="font-bold text-xl text-gray-900 mb-4 flex items-center gap-3">
                                <BookOpen className="w-6 h-6 text-blue-600" />
                                Structure Holistique de l'ODS (10 Sections)
                              </h4>
                              <div className="space-y-3">
                                {[
                                  { num: 1, titre: 'Présentation de l\'offre', desc: 'Vision BZ, valeur ajoutée, principes de service', norme: 'COBIT APO01' },
                                  { num: 2, titre: 'Catalogue de services', desc: 'Support, gestion, conseil, formation, sécurité', norme: 'ITIL 4 Service Value System' },
                                  { num: 3, titre: 'Niveaux de service (SLA)', desc: 'Disponibilité, performance, temps de réponse', norme: 'ITIL 4 Service Level Management' },
                                  { num: 4, titre: 'Modèle d\'engagement', desc: 'Type contrat, heures incluses, flexibilité', norme: 'COBIT APO08' },
                                  { num: 5, titre: 'Organisation & gouvernance', desc: 'Équipe RACI, comités, communication', norme: 'COBIT EDM01, ITIL 4 Governance' },
                                  { num: 6, titre: 'Outils & technologies', desc: 'Ticketing, monitoring, portail client', norme: 'ITIL 4 Service Integration' },
                                  { num: 7, titre: 'Tarification', desc: 'Forfait base, services additionnels, modèles', norme: 'COBIT APO02' },
                                  { num: 8, titre: 'Indicateurs de performance', desc: 'KPIs, CSAT, NPS, MTTR, disponibilité', norme: 'ITIL 4 Measurement & Reporting' },
                                  { num: 9, titre: 'Processus & méthodologies', desc: 'Gestion incidents, changements, problèmes', norme: 'ITIL 4 Practices, COBIT DSS02' },
                                  { num: 10, titre: 'Engagement & garanties', desc: 'Garanties, pénalités, réclamations, résiliation', norme: 'COBIT APO10, ISO 27001 A.15' }
                                ].map((section) => (
                                  <div key={section.num} className="bg-white rounded-lg p-4 border-2 border-blue-200">
                                    <div className="flex items-start gap-3">
                                      <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">
                                        {section.num}
                                      </div>
                                      <div className="flex-1">
                                        <h5 className="font-bold text-gray-900 mb-1">{section.titre}</h5>
                                        <p className="text-sm text-gray-700 mb-2">{section.desc}</p>
                                        <p className="text-xs text-blue-600 font-semibold">{section.norme}</p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* ALIGNEMENT AVEC LES NORMES */}
                            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-6">
                              <h4 className="font-bold text-xl text-gray-900 mb-4 flex items-center gap-3">
                                <Shield className="w-6 h-6 text-green-600" />
                                Alignement avec les Standards Internationaux
                              </h4>
                              <div className="grid md:grid-cols-2 gap-4">
                                <div className="bg-white rounded-lg p-4 border-2 border-green-200">
                                  <h5 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                                    <Target className="w-5 h-5" />
                                    ITIL 4 (Service Management)
                                  </h5>
                                  <ul className="text-sm text-gray-700 space-y-1">
                                    <li>• Service Level Management (SLA)</li>
                                    <li>• Incident Management (P1-P4)</li>
                                    <li>• Change Management</li>
                                    <li>• Problem Management</li>
                                    <li>• Service Catalog Management</li>
                                    <li>• Service Request Management</li>
                                  </ul>
                                </div>
                                <div className="bg-white rounded-lg p-4 border-2 border-green-200">
                                  <h5 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                                    <Award className="w-5 h-5" />
                                    COBIT 2019 (Gouvernance)
                                  </h5>
                                  <ul className="text-sm text-gray-700 space-y-1">
                                    <li>• APO01 - Gérer le programme TI</li>
                                    <li>• APO08 - Gérer les relations</li>
                                    <li>• APO10 - Gérer les fournisseurs</li>
                                    <li>• DSS02 - Gérer les demandes de service</li>
                                    <li>• DSS05 - Gérer les services de sécurité</li>
                                    <li>• EDM01 - Assurer la gouvernance</li>
                                  </ul>
                                </div>
                                <div className="bg-white rounded-lg p-4 border-2 border-green-200">
                                  <h5 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                                    <Shield className="w-5 h-5" />
                                    NIST CSF 2.0 (Cybersécurité)
                                  </h5>
                                  <ul className="text-sm text-gray-700 space-y-1">
                                    <li>• RS - Respond (Réponse incidents)</li>
                                    <li>• RC - Recover (Récupération)</li>
                                    <li>• PR - Protect (Protection)</li>
                                    <li>• DE - Detect (Détection)</li>
                                    <li>• GV - Govern (Gouvernance)</li>
                                  </ul>
                                </div>
                                <div className="bg-white rounded-lg p-4 border-2 border-green-200">
                                  <h5 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5" />
                                    ISO 27001 (SMSI)
                                  </h5>
                                  <ul className="text-sm text-gray-700 space-y-1">
                                    <li>• A.15 - Relations avec les fournisseurs</li>
                                    <li>• A.16 - Gestion des incidents</li>
                                    <li>• A.17 - Continuité d'activité</li>
                                    <li>• A.18 - Conformité</li>
                                  </ul>
                                </div>
                              </div>
                            </div>

                            {/* WORKFLOW COMPLET */}
                            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-300 rounded-lg p-6">
                              <h4 className="font-bold text-xl text-gray-900 mb-4 flex items-center gap-3">
                                <Workflow className="w-6 h-6 text-indigo-600" />
                                Workflow Complet: De la Prise de Besoin à l'ODS
                              </h4>
                              <div className="space-y-4">
                                <div className="bg-white rounded-lg p-4 border-2 border-indigo-200">
                                  <div className="flex items-start gap-3">
                                    <div className="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">1</div>
                                    <div className="flex-1">
                                      <h5 className="font-bold text-gray-900 mb-2">Phase 1: Prise de Besoin Holistique</h5>
                                      <p className="text-sm text-gray-700 mb-2">
                                        Remplir le questionnaire structuré en 5 phases:
                                      </p>
                                      <ul className="text-sm text-gray-600 space-y-1 ml-4">
                                        <li>• Phase 1: Qualification client (contexte, besoins)</li>
                                        <li>• Phase 2: Posture technique (infrastructure, sécurité)</li>
                                        <li>• Phase 3: Gouvernance & support (modèle, SLA)</li>
                                        <li>• Phase 4: Métier spécifique (contexte d'affaires)</li>
                                        <li>• Phase 5: Validation & synthèse</li>
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                                <div className="bg-white rounded-lg p-4 border-2 border-indigo-200">
                                  <div className="flex items-start gap-3">
                                    <div className="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">2</div>
                                    <div className="flex-1">
                                      <h5 className="font-bold text-gray-900 mb-2">Phase 2: Analyse IA PRIsME</h5>
                                      <p className="text-sm text-gray-700 mb-2">
                                        L'IA analyse toutes les réponses et identifie:
                                      </p>
                                      <ul className="text-sm text-gray-600 space-y-1 ml-4">
                                        <li>• Besoins critiques et prioritaires</li>
                                        <li>• Modèle de support optimal</li>
                                        <li>• SLA appropriés selon la criticité</li>
                                        <li>• Services à inclure/exclure</li>
                                        <li>• Tarification recommandée</li>
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                                <div className="bg-white rounded-lg p-4 border-2 border-indigo-200">
                                  <div className="flex items-start gap-3">
                                    <div className="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">3</div>
                                    <div className="flex-1">
                                      <h5 className="font-bold text-gray-900 mb-2">Phase 3: Génération Automatique ODS</h5>
                                      <p className="text-sm text-gray-700 mb-2">
                                        PRIsME génère automatiquement un ODS complet avec:
                                      </p>
                                      <ul className="text-sm text-gray-600 space-y-1 ml-4">
                                        <li>• 10 sections structurées selon les normes</li>
                                        <li>• Catalogue de services détaillé</li>
                                        <li>• SLA alignés ITIL 4</li>
                                        <li>• Matrice RACI (gouvernance COBIT)</li>
                                        <li>• KPIs et métriques de performance</li>
                                        <li>• Tarification transparente</li>
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                                <div className="bg-white rounded-lg p-4 border-2 border-indigo-200">
                                  <div className="flex items-start gap-3">
                                    <div className="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">4</div>
                                    <div className="flex-1">
                                      <h5 className="font-bold text-gray-900 mb-2">Phase 4: Révision & Personnalisation</h5>
                                      <p className="text-sm text-gray-700 mb-2">
                                        L'expert BZ révise et personnalise:
                                      </p>
                                      <ul className="text-sm text-gray-600 space-y-1 ml-4">
                                        <li>• Ajustements selon contexte client</li>
                                        <li>• Validation des SLA et tarifs</li>
                                        <li>• Ajout d'exclusions spécifiques</li>
                                        <li>• Finalisation avec le client</li>
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* TEMPLATE ODS SPÉCIFIQUE */}
                            <div className="bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-300 rounded-lg p-6">
                              <h4 className="font-bold text-xl text-gray-900 mb-4 flex items-center gap-3">
                                <FileText className="w-6 h-6 text-orange-600" />
                                Template ODS Support & Gestion TI
                              </h4>
                              <p className="text-gray-700 leading-relaxed mb-4">
                                Le template <strong>"ODS – Support & gestion TI"</strong> structure la prise de besoin en 5 phases spécifiques:
                              </p>
                              <div className="space-y-3">
                                {[
                                  { phase: 'Phase 1', titre: 'Offre commerciale structurée', contenu: 'Introduction, contexte client, recommandations, plan de travail (Implanter, Gérer, Optimiser)' },
                                  { phase: 'Phase 2', titre: 'Catalogue de services', contenu: 'Services distinctifs, infogérance, cloud/sauvegardes, équipements/licences (tableaux modifiables)' },
                                  { phase: 'Phase 3', titre: 'Tarification détaillée', contenu: 'Tableau de tarification, mensualité totale, frais uniques, notes tarifaires' },
                                  { phase: 'Phase 4', titre: 'Contrat & conditions', contenu: 'Modèle de support, besoins formation, exclusions majeures, SLA' },
                                  { phase: 'Phase 5', titre: 'Annexes pédagogiques', contenu: 'Expertise BZ, programmes spécialisés, lexique, annexes complémentaires' }
                                ].map((item) => (
                                  <div key={item.phase} className="bg-white rounded-lg p-4 border-2 border-orange-200">
                                    <div className="flex items-start gap-3">
                                      <div className="bg-orange-600 text-white rounded-lg px-3 py-1 font-bold text-sm flex-shrink-0">
                                        {item.phase}
                                      </div>
                                      <div className="flex-1">
                                        <h5 className="font-bold text-gray-900 mb-1">{item.titre}</h5>
                                        <p className="text-sm text-gray-700">{item.contenu}</p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* CONDITIONS DE GÉNÉRATION AUTOMATIQUE */}
                            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-300 rounded-lg p-6">
                              <h4 className="font-bold text-xl text-gray-900 mb-4 flex items-center gap-3">
                                <Clock className="w-6 h-6 text-yellow-600" />
                                Quand l'ODS est Généré Automatiquement?
                              </h4>
                              <div className="bg-white rounded-lg p-4 border-2 border-yellow-200 mb-4">
                                <p className="text-gray-700 leading-relaxed mb-3">
                                  L'ODS est généré automatiquement lorsque le modèle de support sélectionné n'est <strong>PAS</strong> "Support interne - notre équipe gère".
                                </p>
                                <div className="grid md:grid-cols-2 gap-3">
                                  <div className="bg-green-50 rounded-lg p-3 border border-green-300">
                                    <p className="text-sm font-bold text-green-900 mb-2">✓ ODS Généré:</p>
                                    <ul className="text-sm text-gray-700 space-y-1">
                                      <li>• Support réactif</li>
                                      <li>• Support proactif</li>
                                      <li>• Support dédié</li>
                                      <li>• Support hybride</li>
                                    </ul>
                                  </div>
                                  <div className="bg-red-50 rounded-lg p-3 border border-red-300">
                                    <p className="text-sm font-bold text-red-900 mb-2">✗ ODS Non Généré:</p>
                                    <ul className="text-sm text-gray-700 space-y-1">
                                      <li>• Support interne uniquement</li>
                                    </ul>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* EXEMPLE CONCRET AVEC NORMES */}
                            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-6">
                              <h4 className="font-bold text-xl text-gray-900 mb-4 flex items-center gap-3">
                                <BarChart3 className="w-6 h-6 text-green-600" />
                                Exemple Concret Aligné avec les Normes
                              </h4>
                              <div className="bg-white rounded-lg p-5 border-2 border-green-200">
                                <p className="text-sm text-gray-600 italic mb-3">PME 50 employés, secteur manufacturier - ODS généré automatiquement:</p>
                                <div className="space-y-3">
                                  <div className="border-l-4 border-green-500 pl-4">
                                    <h5 className="font-bold text-gray-900 mb-1">Service: Directeur TI à temps partiel</h5>
                                    <p className="text-sm text-gray-700">2 jours/semaine, conseil stratégique, gouvernance TI</p>
                                    <p className="text-xs text-green-600 mt-1">Norme: COBIT APO01, ITIL 4 Service Management</p>
                                  </div>
                                  <div className="border-l-4 border-blue-500 pl-4">
                                    <h5 className="font-bold text-gray-900 mb-1">SLA (Service Level Agreement)</h5>
                                    <ul className="text-sm text-gray-700 space-y-1">
                                      <li>• P1 (Critique): Réponse &lt;2h, résolution &lt;4h</li>
                                      <li>• P2 (Majeur): Réponse &lt;4h, résolution &lt;24h</li>
                                      <li>• P3 (Mineur): Réponse &lt;24h, résolution &lt;72h</li>
                                      <li>• Disponibilité: 99.5% (hors maintenance planifiée)</li>
                                    </ul>
                                    <p className="text-xs text-blue-600 mt-1">Norme: ITIL 4 Service Level Management</p>
                                  </div>
                                  <div className="border-l-4 border-purple-500 pl-4">
                                    <h5 className="font-bold text-gray-900 mb-1">Livrables Mensuels</h5>
                                    <ul className="text-sm text-gray-700 space-y-1">
                                      <li>• Comité TI mensuel (90 min)</li>
                                      <li>• Roadmap trimestriel</li>
                                      <li>• Veille technologique</li>
                                      <li>• Rapport KPIs (SLA, incidents, satisfaction)</li>
                                    </ul>
                                    <p className="text-xs text-purple-600 mt-1">Norme: COBIT APO08, ITIL 4 Measurement & Reporting</p>
                                  </div>
                                  <div className="border-l-4 border-orange-500 pl-4">
                                    <h5 className="font-bold text-gray-900 mb-1">Tarification</h5>
                                    <p className="text-sm text-gray-700">6 000$/mois forfait (inclus 16h/mois)</p>
                                    <p className="text-xs text-orange-600 mt-1">Norme: COBIT APO02</p>
                                  </div>
                                  <div className="border-l-4 border-red-500 pl-4">
                                    <h5 className="font-bold text-gray-900 mb-1">Exclusions</h5>
                                    <ul className="text-sm text-gray-700 space-y-1">
                                      <li>• Développement logiciel sur mesure</li>
                                      <li>• Support niveau 1 (helpdesk utilisateur)</li>
                                      <li>• Déplacements hors région</li>
                                    </ul>
                                    <p className="text-xs text-red-600 mt-1">Norme: ITIL 4 Service Catalog Management</p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* MESSAGE DANY */}
                            <div className="bg-gradient-to-r from-orange-600 via-red-600 to-orange-600 rounded-lg p-6 text-white">
                              <p className="text-lg font-bold mb-3">💎 LE MESSAGE DANY:</p>
                              <p className="leading-relaxed mb-3">
                                "Un ODS, c'est pas un document légal compliqué. C'est une <strong>PROMESSE CLAIRE</strong>. Le client doit lire ça et se dire: '<strong>OK, je comprends exactement ce que je vais recevoir.</strong>' Si c'est pas le cas, recommence. L'ODS, c'est la FONDATION de la confiance."
                              </p>
                              <p className="leading-relaxed text-white/90">
                                "Avec PRIsME, on génère un ODS professionnel en quelques minutes. L'IA fait le travail lourd, 
                                mais c'est nous qui ajoutons la touche humaine, le contexte, la relation. 
                                <strong> C'est ça, l'excellence BZ.</strong>"
                              </p>
                            </div>
                          </div>
                        )}

                        {/* ONGLET 2: SPÉCIFICATIONS D'EXIGENCES */}
                        {activeTab === 'cahier' && (
                          <div className="space-y-6">
                            {/* Header */}
                            <div className="flex items-center gap-4 pb-4 border-b-2 border-blue-300">
                              <FileText className="w-10 h-10 text-blue-500" />
                              <div>
                                <h3 className="text-2xl font-bold text-gray-900">Spécifications d'exigences d'affaires et TI</h3>
                                <p className="text-blue-600 font-semibold">Le BLUEPRINT technique du projet</p>
                              </div>
                            </div>

                            {/* C'EST QUOI? */}
                            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                              <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                                <Target className="w-5 h-5 text-blue-600" />
                                C'EST QUOI?
                              </h4>
                              <p className="text-gray-700 leading-relaxed">
                                Les Spécifications d'exigences d'affaires et TI, c'est la <strong>BIBLE du projet</strong>. Chaque fonctionnalité, chaque contrainte, chaque exigence. Si c'est pas dans les Spécifications d'exigences, <strong>c'est pas dans le projet</strong>.
                              </p>
                            </div>

                            {/* POURQUOI C'EST CRITIQUE? */}
                            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                              <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                                <Zap className="w-5 h-5 text-red-600" />
                                POURQUOI C'EST CRITIQUE?
                              </h4>
                              <p className="text-gray-700 leading-relaxed mb-3">
                                Sans Spécifications d'exigences, c'est le <strong>CHAOS</strong>. L'équipe technique ne sait pas quoi construire. Le client ajoute des features en cours de route. Le scope explose. Les coûts explosent.
                              </p>
                              <p className="text-gray-700 leading-relaxed font-bold">
                                De bonnes Spécifications d'exigences = Zéro ambiguïté = Projet livré on time, on budget.
                              </p>
                            </div>

                            {/* QU'EST-CE QU'ON Y MET? */}
                            <div className="bg-white border-2 border-blue-200 rounded-lg p-4">
                              <h4 className="font-bold text-lg text-gray-900 mb-3">📋 QU'EST-CE QU'ON Y MET?</h4>
                              <ul className="space-y-2">
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <strong className="text-gray-900">Contexte & Objectifs:</strong>
                                    <span className="text-gray-700"> Pourquoi ce projet existe? Quel problème on résout?</span>
                                  </div>
                                </li>
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <strong className="text-gray-900">Spécifications fonctionnelles:</strong>
                                    <span className="text-gray-700"> Chaque feature décrite en DÉTAIL (user stories, cas d'usage)</span>
                                  </div>
                                </li>
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <strong className="text-gray-900">Contraintes techniques:</strong>
                                    <span className="text-gray-700"> Technologies obligatoires, intégrations requises, performance</span>
                                  </div>
                                </li>
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <strong className="text-gray-900">Critères d'acceptation:</strong>
                                    <span className="text-gray-700"> Comment on SAIT que c'est fini? (Tests, métriques)</span>
                                  </div>
                                </li>
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <strong className="text-gray-900">Hors scope (OUT):</strong>
                                    <span className="text-gray-700"> Ce qu'on NE fait PAS (pour éviter le scope creep)</span>
                                  </div>
                                </li>
                              </ul>
                            </div>

                            {/* EXEMPLE CONCRET */}
                            <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
                              <h4 className="font-bold text-lg text-gray-900 mb-2">✅ EXEMPLE CONCRET</h4>
                              <p className="text-sm text-gray-600 italic mb-2">Projet: Portail employé self-service RH</p>
                              <div className="bg-white rounded p-3 text-sm space-y-1 text-gray-700">
                                <p>• <strong>Objectif:</strong> Réduire de 70% les requêtes RH (paie, vacances, avantages)</p>
                                <p>• <strong>Features:</strong> Consultation paie, demande congé, mise à jour infos perso</p>
                                <p>• <strong>Contrainte:</strong> Intégration API paie Nethris, SSO Azure AD</p>
                                <p>• <strong>Acceptation:</strong> 90% des employés adoptent en 3 mois</p>
                                <p>• <strong>OUT:</strong> Gestion recrutement, évaluations performance</p>
                              </div>
                            </div>

                            {/* ERREURS À ÉVITER */}
                            <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4">
                              <h4 className="font-bold text-lg text-red-900 mb-3 flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5" />
                                ❌ ERREURS À ÉVITER
                              </h4>
                              <ul className="space-y-2 text-gray-700">
                                <li className="flex items-start gap-2">
                                  <span className="text-red-600 font-bold mt-0.5">✗</span>
                                  <span><strong>Trop générique:</strong> "Un système intuitif et performant" ← Dit RIEN!</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-red-600 font-bold mt-0.5">✗</span>
                                  <span><strong>Pas de critères mesurables:</strong> Impossible de savoir si c'est fini</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-red-600 font-bold mt-0.5">✗</span>
                                  <span><strong>Oublier le hors-scope:</strong> Le client va demander "juste une petite chose"</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-red-600 font-bold mt-0.5">✗</span>
                                  <span><strong>Pas validé par le client:</strong> Tu construis ce que TOI tu penses qu'il veut</span>
                                </li>
                              </ul>
                            </div>

                            {/* MESSAGE DANY */}
                            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-600 rounded-lg p-6 text-white">
                              <p className="text-lg font-bold mb-3">💎 LE MESSAGE DANY:</p>
                              <p className="leading-relaxed">
                                "Les Spécifications d'exigences, c'est ton <strong>BOUCLIER</strong> contre le scope creep. Quand le client dit '<strong>Mais on avait dit que...</strong>', tu sors les Spécifications d'exigences et tu montres: '<strong>Non, regarde, c'est écrit ici exactement ce qui était prévu.</strong>' C'est ça, la rigueur."
                              </p>
                            </div>
                          </div>
                        )}

                        {/* ONGLET 3: ROADMAP ANNUEL */}
                        {activeTab === 'roadmap' && (
                          <div className="space-y-6">
                            {/* Header */}
                            <div className="flex items-center gap-4 pb-4 border-b-2 border-green-300">
                              <Calendar className="w-10 h-10 text-green-500" />
                              <div>
                                <h3 className="text-2xl font-bold text-gray-900">Roadmap Annuel</h3>
                                <p className="text-green-600 font-semibold">Le PLAN D'EXÉCUTION sur 12 mois</p>
                              </div>
                            </div>

                            {/* C'EST QUOI? */}
                            <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
                              <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                                <Target className="w-5 h-5 text-green-600" />
                                C'EST QUOI?
                              </h4>
                              <p className="text-gray-700 leading-relaxed">
                                Le Roadmap, c'est le <strong>CALENDRIER D'EXÉCUTION</strong>. Quels projets TI on lance, quand, avec quelles ressources. C'est la traduction TACTIQUE du Plan Directeur (vision 3-5 ans) en <strong>actions concrètes cette année</strong>.
                              </p>
                            </div>

                            {/* POURQUOI C'EST CRITIQUE? */}
                            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                              <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                                <Zap className="w-5 h-5 text-red-600" />
                                POURQUOI C'EST CRITIQUE?
                              </h4>
                              <p className="text-gray-700 leading-relaxed mb-3">
                                Sans Roadmap, les projets TI sont <strong>RÉACTIFS au lieu de PROACTIFS</strong>. On éteint des feux. On répond aux urgences. Mais on ne fait jamais avancer la stratégie.
                              </p>
                              <p className="text-gray-700 leading-relaxed font-bold">
                                Un bon Roadmap = Priorisation claire = Ressources optimisées = Valeur maximale.
                              </p>
                            </div>

                            {/* QU'EST-CE QU'ON Y MET? */}
                            <div className="bg-white border-2 border-green-200 rounded-lg p-4">
                              <h4 className="font-bold text-lg text-gray-900 mb-3">📋 QU'EST-CE QU'ON Y MET?</h4>
                              <ul className="space-y-2">
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <strong className="text-gray-900">Projets par trimestre (Q1-Q4):</strong>
                                    <span className="text-gray-700"> Liste priorisée des initiatives TI</span>
                                  </div>
                                </li>
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <strong className="text-gray-900">Jalons & livrables:</strong>
                                    <span className="text-gray-700"> Quand est-ce qu'on livre quoi? (dates clés)</span>
                                  </div>
                                </li>
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <strong className="text-gray-900">Ressources allouées:</strong>
                                    <span className="text-gray-700"> Budget, équipe, fournisseurs externes</span>
                                  </div>
                                </li>
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <strong className="text-gray-900">Dépendances:</strong>
                                    <span className="text-gray-700"> Projet B ne peut pas commencer avant Projet A</span>
                                  </div>
                                </li>
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <strong className="text-gray-900">Indicateurs de succès:</strong>
                                    <span className="text-gray-700"> Comment mesurer le progrès?</span>
                                  </div>
                                </li>
                              </ul>
                            </div>

                            {/* EXEMPLE CONCRET */}
                            <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
                              <h4 className="font-bold text-lg text-gray-900 mb-2">✅ EXEMPLE CONCRET</h4>
                              <p className="text-sm text-gray-600 italic mb-2">PME secteur distribution, roadmap 2025:</p>
                              <div className="bg-white rounded p-3 text-sm space-y-2 text-gray-700">
                                <p><strong>Q1:</strong> Migration cloud Azure (ERP + CRM) - 80k$ - Équipe 3 personnes</p>
                                <p><strong>Q2:</strong> Cybersécurité (MFA, EDR) - 25k$ - Consultant externe</p>
                                <p><strong>Q3:</strong> Automatisation ventes (intégration Shopify-ERP) - 40k$</p>
                                <p><strong>Q4:</strong> BI & Analytics (PowerBI dashboards) - 30k$</p>
                                <p className="pt-2 border-t"><strong>Total:</strong> 175k$ investissement TI 2025</p>
                              </div>
                            </div>

                            {/* ERREURS À ÉVITER */}
                            <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4">
                              <h4 className="font-bold text-lg text-red-900 mb-3 flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5" />
                                ❌ ERREURS À ÉVITER
                              </h4>
                              <ul className="space-y-2 text-gray-700">
                                <li className="flex items-start gap-2">
                                  <span className="text-red-600 font-bold mt-0.5">✗</span>
                                  <span><strong>Trop ambitieux:</strong> 20 projets en 12 mois = RIEN ne sera fini</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-red-600 font-bold mt-0.5">✗</span>
                                  <span><strong>Pas de buffer:</strong> Imprévus vont arriver, prévois 20% de marge</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-red-600 font-bold mt-0.5">✗</span>
                                  <span><strong>Projets sans priorité:</strong> Si tout est urgent, RIEN n'est urgent</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-red-600 font-bold mt-0.5">✗</span>
                                  <span><strong>Pas de révision:</strong> Le Roadmap doit être ajusté chaque trimestre</span>
                                </li>
                              </ul>
                            </div>

                            {/* MESSAGE DANY */}
                            <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-green-600 rounded-lg p-6 text-white">
                              <p className="text-lg font-bold mb-3">💎 LE MESSAGE DANY:</p>
                              <p className="leading-relaxed">
                                "Un Roadmap, c'est un <strong>GUIDE, pas une PRISON</strong>. Des imprévus vont arriver. Des priorités vont changer. C'est NORMAL. Mais si tu pars avec un plan solide, tes ajustements seront <strong>stratégiques, pas paniqués</strong>. C'est ça, l'agilité intelligente."
                              </p>
                            </div>
                          </div>
                        )}

                        {/* ONGLET 4: PLAN DIRECTEUR */}
                        {activeTab === 'plan' && (
                          <div className="space-y-6">
                            {/* Header */}
                            <div className="flex items-center gap-4 pb-4 border-b-2 border-purple-300">
                              <Map className="w-10 h-10 text-purple-500" />
                              <div>
                                <h3 className="text-2xl font-bold text-gray-900">Plan Directeur TI</h3>
                                <p className="text-purple-600 font-semibold">La VISION STRATÉGIQUE 3-5 ans</p>
                              </div>
                            </div>

                            {/* C'EST QUOI? */}
                            <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg">
                              <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                                <Target className="w-5 h-5 text-purple-600" />
                                C'EST QUOI?
                              </h4>
                              <p className="text-gray-700 leading-relaxed">
                                Le Plan Directeur, c'est la <strong>VISION à LONG TERME</strong> de l'évolution TI de l'entreprise. Où on veut être dans 3-5 ans? Quelles technologies? Quelles capacités? C'est la <strong>BOUSSOLE stratégique</strong> qui guide TOUTES les décisions TI.
                              </p>
                            </div>

                            {/* POURQUOI C'EST CRITIQUE? */}
                            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                              <h4 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
                                <Zap className="w-5 h-5 text-red-600" />
                                POURQUOI C'EST CRITIQUE?
                              </h4>
                              <p className="text-gray-700 leading-relaxed mb-3">
                                Sans Plan Directeur, les investissements TI sont <strong>DÉCOUSUS</strong>. On achète des outils qui ne parlent pas ensemble. On investit dans des technologies qui seront obsolètes. On ne construit pas de <strong>FONDATIONS solides</strong>.
                              </p>
                              <p className="text-gray-700 leading-relaxed font-bold">
                                Un bon Plan Directeur = Cohérence = Scalabilité = ROI maximal à long terme.
                              </p>
                            </div>

                            {/* QU'EST-CE QU'ON Y MET? */}
                            <div className="bg-white border-2 border-purple-200 rounded-lg p-4">
                              <h4 className="font-bold text-lg text-gray-900 mb-3">📋 QU'EST-CE QU'ON Y MET?</h4>
                              <ul className="space-y-2">
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <strong className="text-gray-900">Alignement stratégique:</strong>
                                    <span className="text-gray-700"> Comment la TI supporte les objectifs d'affaires?</span>
                                  </div>
                                </li>
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <strong className="text-gray-900">Architecture cible:</strong>
                                    <span className="text-gray-700"> Infrastructure, applications, données (État futur vs actuel)</span>
                                  </div>
                                </li>
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <strong className="text-gray-900">Priorités d'investissement:</strong>
                                    <span className="text-gray-700"> Modernisation? Cloud? Cybersécurité? Analytics?</span>
                                  </div>
                                </li>
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <strong className="text-gray-900">Capacités organisationnelles:</strong>
                                    <span className="text-gray-700"> Équipe, compétences, gouvernance à développer</span>
                                  </div>
                                </li>
                                <li className="flex items-start gap-3">
                                  <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <strong className="text-gray-900">Budget pluriannuel:</strong>
                                    <span className="text-gray-700"> Enveloppe TI sur 3-5 ans (CAPEX + OPEX)</span>
                                  </div>
                                </li>
                              </ul>
                            </div>

                            {/* EXEMPLE CONCRET */}
                            <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4">
                              <h4 className="font-bold text-lg text-gray-900 mb-2">✅ EXEMPLE CONCRET</h4>
                              <p className="text-sm text-gray-600 italic mb-2">Entreprise 200 employés, Plan Directeur 2025-2028:</p>
                              <div className="bg-white rounded p-3 text-sm space-y-2 text-gray-700">
                                <p><strong>Vision:</strong> "Devenir data-driven avec infrastructure cloud-first sécurisée"</p>
                                <p><strong>2025:</strong> Migration cloud (Azure) + cybersécurité baseline</p>
                                <p><strong>2026:</strong> Automatisation processus + plateforme BI</p>
                                <p><strong>2027:</strong> IA/ML pour prédictions ventes + support automatisé</p>
                                <p><strong>2028:</strong> Architecture microservices + API-first</p>
                                <p className="pt-2 border-t"><strong>Investissement:</strong> 1.2M$ sur 4 ans (300k$/an)</p>
                              </div>
                            </div>

                            {/* ERREURS À ÉVITER */}
                            <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4">
                              <h4 className="font-bold text-lg text-red-900 mb-3 flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5" />
                                ❌ ERREURS À ÉVITER
                              </h4>
                              <ul className="space-y-2 text-gray-700">
                                <li className="flex items-start gap-2">
                                  <span className="text-red-600 font-bold mt-0.5">✗</span>
                                  <span><strong>Hype-driven:</strong> "On va tout mettre en blockchain IA quantum" ← Ridicule</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-red-600 font-bold mt-0.5">✗</span>
                                  <span><strong>Pas aligné business:</strong> Plan TI fait en silo, sans les dirigeants</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-red-600 font-bold mt-0.5">✗</span>
                                  <span><strong>Trop détaillé:</strong> C'est une VISION, pas un plan projet détaillé</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-red-600 font-bold mt-0.5">✗</span>
                                  <span><strong>Jamais révisé:</strong> Le monde change, ton plan doit évoluer aussi</span>
                                </li>
                              </ul>
                            </div>

                            {/* MESSAGE DANY */}
                            <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-600 rounded-lg p-6 text-white">
                              <p className="text-lg font-bold mb-3">💎 LE MESSAGE DANY:</p>
                              <p className="leading-relaxed">
                                "Le Plan Directeur, c'est ce qui sépare les <strong>LEADERS TI stratégiques</strong> des 'techniciens qui réparent des ordis'. Si tu comprends où ton client veut aller dans 5 ans et que tu construis le CHEMIN pour y arriver, tu deviens <strong>INDISPENSABLE</strong>. C'est ça, la vraie valeur BZ."
                              </p>
                            </div>
                          </div>
                        )}

                      </div>
                    </div>

                    {/* AI Insight */}
                    <div className="bg-blue-50 border-l-4 border-bz-blue p-4 rounded-r-lg">
                      <div className="flex items-start gap-3">
                        <Brain className="w-6 h-6 text-bz-blue flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="font-semibold text-bz-dark mb-2">AI Insight</h3>
                          <p className="text-gray-700">{item.aiSuggestion}</p>
                          <p className="text-gray-700 mt-2">
                            L'IA peut analyser les historiques clients pour suggérer automatiquement 
                            le type de livrable le plus pertinent selon la taille, l'industrie et la maturité TI.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* RACI Spécifique */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <h3 className="font-semibold text-bz-dark bg-gray-50 p-4 border-b">
                        Matrice RACI - Prise de Besoin Client
                      </h3>
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left p-3 border-b">Rôle</th>
                            <th className="text-center p-3 border-b">R</th>
                            <th className="text-center p-3 border-b">A</th>
                            <th className="text-center p-3 border-b">C</th>
                            <th className="text-center p-3 border-b">I</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b">
                            <td className="p-3 text-gray-700">Prime (Directeur TI)</td>
                            <td className="text-center text-bz-blue font-bold">✓</td>
                            <td className="text-center text-bz-blue font-bold">✓</td>
                            <td className="text-center"></td>
                            <td className="text-center"></td>
                          </tr>
                          <tr className="border-b">
                            <td className="p-3 text-gray-700">Chargé de projet</td>
                            <td className="text-center">•</td>
                            <td className="text-center"></td>
                            <td className="text-center text-bz-blue font-bold">✓</td>
                            <td className="text-center"></td>
                          </tr>
                          <tr className="border-b">
                            <td className="p-3 text-gray-700">Architecte Solution</td>
                            <td className="text-center">•</td>
                            <td className="text-center"></td>
                            <td className="text-center text-bz-blue font-bold">✓</td>
                            <td className="text-center"></td>
                          </tr>
                          <tr>
                            <td className="p-3 text-gray-700">Client (Décideur)</td>
                            <td className="text-center"></td>
                            <td className="text-center"></td>
                            <td className="text-center text-bz-blue font-bold">✓</td>
                            <td className="text-center"></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : isDepartmentResponsibilities ? (
                  <>
                    <div className="bg-blue-50 border-l-4 border-bz-blue p-4 rounded-r-lg">
                      <div className="flex items-start gap-3">
                        <Brain className="w-6 h-6 text-bz-blue flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="font-semibold text-bz-dark mb-2">Objectif</h3>
                          <p className="text-gray-700">{item.aiSuggestion}</p>
                        </div>
                      </div>
                    </div>

                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <h3 className="font-semibold text-bz-dark bg-gray-50 p-4 border-b">
                        Responsabilités par département
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[900px] text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left p-3 border-b">Département</th>
                              <th className="text-left p-3 border-b">Responsabilité principale</th>
                              <th className="text-left p-3 border-b">Exemples de dossiers</th>
                              <th className="text-left p-3 border-b">Règle de clarification</th>
                            </tr>
                          </thead>
                          <tbody>
                            {departmentResponsibilities.map((row) => (
                              <tr key={row.department} className="border-b last:border-b-0 align-top">
                                <td className="p-3 font-bold text-bz-dark">{row.department}</td>
                                <td className="p-3 text-gray-700">{row.responsibility}</td>
                                <td className="p-3 text-gray-700">{row.examples}</td>
                                <td className="p-3 text-gray-700">{row.clarification}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg">
                      <div className="flex items-start gap-3">
                        <Shield className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="font-semibold text-bz-dark mb-2">Responsable de clarification</h3>
                          <p className="text-gray-700">
                            Jean-Michel Caouette, directeur adjoint, est responsable de clarifier les zones grises,
                            d’aligner les départements et de confirmer où chaque responsabilité doit être portée.
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* AI Insight */}
                    <div className="bg-blue-50 border-l-4 border-bz-blue p-4 rounded-r-lg">
                      <div className="flex items-start gap-3">
                        <Brain className="w-6 h-6 text-bz-blue flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="font-semibold text-bz-dark mb-2">AI Insight</h3>
                          <p className="text-gray-700">{item.aiSuggestion}</p>
                        </div>
                      </div>
                    </div>

                    {/* KPI/ROI Section */}
                    <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
                      <div className="flex items-start gap-3">
                        <TrendingUp className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                        <div className="flex-1">
                          <h3 className="font-semibold text-bz-dark mb-3">Performance KPI</h3>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Score actuel</span>
                              <span className="text-lg font-bold text-green-600">{item.kpiScore}%</span>
                            </div>
                            <div className="bg-gray-200 rounded-full h-3">
                              <div
                                style={{ width: `${item.kpiScore}%` }}
                                className="bg-green-500 h-3 rounded-full transition-all duration-1000"
                              />
                            </div>
                            <p className="text-sm text-gray-600 mt-2">
                              ROI estimé: +{Math.round(item.kpiScore * 1.5)}% sur 6 mois
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Coaching Section */}
                    <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg">
                      <div className="flex items-start gap-3">
                        <Users className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="font-semibold text-bz-dark mb-2">Coaching & Développement</h3>
                          <ul className="space-y-1 text-gray-700">
                            <li className="flex items-start gap-2">
                              <span className="text-purple-600">•</span>
                              <span>Organiser des sessions de formation trimestrielles</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-purple-600">•</span>
                              <span>Mettre en place un système de mentorat par les pairs</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-purple-600">•</span>
                              <span>Créer des opportunités de partage de connaissances</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* RACI/Gouvernance */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <h3 className="font-semibold text-bz-dark bg-gray-50 p-4 border-b">
                        Matrice RACI
                      </h3>
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left p-3 border-b">Rôle</th>
                            <th className="text-center p-3 border-b">R</th>
                            <th className="text-center p-3 border-b">A</th>
                            <th className="text-center p-3 border-b">C</th>
                            <th className="text-center p-3 border-b">I</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b">
                            <td className="p-3 text-gray-700">Directeur TI</td>
                            <td className="text-center">•</td>
                            <td className="text-center text-bz-blue font-bold">✓</td>
                            <td className="text-center">•</td>
                            <td className="text-center"></td>
                          </tr>
                          <tr className="border-b">
                            <td className="p-3 text-gray-700">Chef d'équipe</td>
                            <td className="text-center text-bz-blue font-bold">✓</td>
                            <td className="text-center"></td>
                            <td className="text-center">•</td>
                            <td className="text-center">•</td>
                          </tr>
                          <tr>
                            <td className="p-3 text-gray-700">Membres d'équipe</td>
                            <td className="text-center">•</td>
                            <td className="text-center"></td>
                            <td className="text-center text-bz-blue font-bold">✓</td>
                            <td className="text-center">•</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-xl flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="aikup-button-secondary"
                >
                  Fermer
                </button>
                {isComprehensionDesBesoins && (
                  <button 
                    onClick={handleExplorerPlus}
                    className="aikup-button flex items-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    Générer mes livrables
                  </button>
                )}
                {isAméliorationContinue && (
                  <button 
                    onClick={handleExplorerPlus}
                    className="aikup-button flex items-center gap-2"
                  >
                    <TrendingUp className="w-4 h-4" />
                    Explorer les 9 Standards
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
      
      {/* Wizard de prise de besoin */}
      <BesoinsWizard 
        isOpen={isWizardOpen} 
        onClose={() => setIsWizardOpen(false)} 
      />
      
      {/* Modal Amélioration Continue */}
      <AméliorationContinueModal 
        isOpen={isAméliorationOpen} 
        onClose={() => setIsAméliorationOpen(false)} 
      />
    </AnimatePresence>
  );
}
